import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Cpu, Building2, GraduationCap, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import SafeSEO from '@/components/SafeSEO';
import { Link } from 'react-router-dom';
import CtaSection from '@/components/CtaSection';

const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'investment',
      icon: TrendingUp,
      title: t('services.items.investment.title'),
      description: t('services.items.investment.desc'),
      features: t('services.items.investment.features'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/db804f2c8dc930cbf3db0df5ec805563.png',
      color: 'from-[#0A5F7F] to-[#0D7FA3]',
      link: null
    },
    {
      id: 'digital',
      icon: Cpu,
      title: t('services.items.digital.title'),
      description: t('services.items.digital.desc'),
      features: t('services.items.digital.features'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/ecbf5458c163e3982a9872a2945d1b98.png',
      color: 'from-[#0A5F7F] to-[#0D7FA3]',
      link: null
    },
    {
      id: 'talent',
      icon: GraduationCap,
      title: t('services.items.talent.title'),
      description: t('services.items.talent.desc'),
      features: t('services.items.talent.features'),
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f',
      color: 'from-amber-500 to-orange-600',
      link: '/capacitacion'
    },
    {
      id: 'structuring',
      icon: Building2,
      title: t('services.items.structuring.title'),
      description: t('services.items.structuring.desc'),
      features: t('services.items.structuring.features'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/1b4ab00b1e14845dcb24ddbbde9015d8.png',
      color: 'from-[#0A5F7F] to-[#0D7FA3]',
      link: null
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('services.title')}
        description={t('services.subtitle')}
        url="https://consulogic-gt-com-544311.hostingersite.com/servicios"
      />

      <div className="pt-20">
        <section className="relative py-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48 blur-3xl" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('services.title')}</h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed font-light">
                {t('services.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 gap-32">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 70 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`${index % 2 !== 0 ? 'lg:order-2' : ''} p-10 lg:p-14 bg-white rounded-[3rem] border border-gray-100 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] relative overflow-hidden group hover:shadow-[0_48px_96px_-24px_rgba(0,0,0,0.12)] transition-all duration-500`}>
                    <div className={`w-24 h-24 bg-gradient-to-br ${service.color} rounded-[2rem] flex items-center justify-center mb-10 shadow-2xl transition-transform duration-500 group-hover:rotate-6`}>
                      <service.icon className="h-11 w-11 text-white" />
                    </div>
                    <h2 className={`text-4xl font-extrabold mb-8 tracking-tight ${service.id === 'talent' ? 'text-orange-900' : 'text-[#0A5F7F]'}`}>
                      {service.title}
                    </h2>
                    <p className="text-xl text-gray-600 mb-10 leading-relaxed font-medium">
                      {service.description}
                    </p>
                    <div className="space-y-5 mb-10">
                      {Array.isArray(service.features) && service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-4 group/item">
                          <div className={`mt-1 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${service.id === 'talent' ? 'bg-orange-100 text-orange-600' : 'bg-[#0A5F7F]/10 text-[#0A5F7F]'}`}>
                            <CheckCircle className="h-5 w-5" />
                          </div>
                          <span className="text-gray-700 font-semibold text-lg">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {service.link && (
                      <Link to={service.link}>
                        <Button size="lg" className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white rounded-2xl h-14 px-8 text-lg font-bold shadow-lg shadow-orange-200">
                          {t('services.learnMore')}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    )}
                  </div>
                  <div className={`${index % 2 !== 0 ? 'lg:order-1' : ''} relative group`}>
                    <div className={`absolute -inset-6 bg-gradient-to-r ${service.color} rounded-[3rem] blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-700`} />
                    <img
                      className="w-full h-[650px] object-cover rounded-[3rem] shadow-2xl relative z-10 border border-white transition-all duration-700 group-hover:scale-[1.02]"
                      alt={service.title}
                      src={service.image}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                {t('services.customTitle')}
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t('services.customSubtitle')}
              </p>
              <Link to="/contacto">
                <Button size="lg" className="bg-[#0A5F7F] text-white hover:bg-[#0A5F7F]/90">
                  {t('home.scheduleBtn')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;